import { useEffect, useMemo, useState } from 'react';

import { FieldSelectMulti } from '@/components/looker/field-select-multi';
import { Tooltip } from '@/components/ui/tooltip';
import { ListIds } from '@/data-explorer-v2/api/queries/list-ids';
import type { Node } from '@/data-explorer-v2/entities/node';
import { useLevelFilterDevice } from '@/data-explorer-v2/hooks/use-level-filter-device';

import {
  BATTERY_LEVELS,
  type BatteryLevel,
} from '../../entities/battery-level';
import type { AllIdFilters } from '../../entities/filters';

import { DisabledHint } from './disabled-hint';

export type LevelFilterProps = {
  level: BatteryLevel;
  customerLabel: string;
  parentId?: string;
  filters: AllIdFilters;
  onChange: (filters: Partial<AllIdFilters>) => void;
  disabled?: boolean;
  parentLevel?: BatteryLevel;
  parentLabel?: string;
};

export function LevelFilter({
  level,
  customerLabel,
  parentId,
  filters,
  onChange,
  disabled = false,
  parentLevel,
  parentLabel,
}: LevelFilterProps) {
  const listEntities = ListIds.useQuery({
    level,
    parentId,
  });

  const filterKey = `${level}Id` as const;
  const currentValue = filters[filterKey];

  // Local state for batching multi-select changes
  const [selectedValue, setSelectedValue] = useState<string[] | undefined>(
    undefined,
  );

  // Reset local state when filter value changes externally (e.g., hierarchy clearing)
  useEffect(() => {
    if (currentValue === undefined) {
      setSelectedValue(undefined);
    }
  }, [currentValue]);

  const options = useMemo(() => {
    return (
      listEntities.data?.ids.map((node: Node) => ({
        value: node.id,
        label: node.label,
      })) ?? []
    );
  }, [listEntities.data?.ids]);

  // Only apply device-based auto-selection for battery hierarchy levels, not PCS
  // PCS should be manually selected by users without automatic population
  // Check if level is in the battery hierarchy (exclude 'pcs' which is cast to BatteryLevel)
  const isBatteryLevel = BATTERY_LEVELS.includes(level);

  useLevelFilterDevice({
    id: filters.deviceId,
    filterKey,
    level,
    currentValue,
    setSelectedValue,
    onChange,
    disabled: disabled || !isBatteryLevel,
  });

  function handleMultiSelectChange(newValues: string[] = []) {
    // Handle clearing immediately to trigger hierarchy cleanup
    if (newValues.length === 0) {
      onChange({ [filterKey]: undefined });
      setSelectedValue(undefined);
      return;
    }

    // Batch non-empty selections
    setSelectedValue(newValues);
  }

  function handleMultiSelectPersist() {
    if (selectedValue === undefined) {
      return;
    }

    onChange({
      [filterKey]: selectedValue.length === 0 ? undefined : selectedValue,
    });
    setSelectedValue(undefined);
  }

  // Use local state if available, otherwise fall back to current filter value
  const displayValue =
    selectedValue ??
    (Array.isArray(currentValue)
      ? currentValue
      : currentValue
        ? [currentValue]
        : []);

  const selectComponent = (
    <FieldSelectMulti
      data-testid={`data-explorer-${level}-filter`}
      className="max-w-[250px]"
      label={customerLabel}
      placeholder={`Filter by ${customerLabel}`}
      values={displayValue}
      options={options}
      onChange={handleMultiSelectChange}
      onClose={handleMultiSelectPersist}
      isLoading={listEntities.isFetching}
      disabled={disabled || listEntities.isFetching || !parentId}
      limit={5}
      autoResize={true}
    />
  );

  // Wrap with tooltip when disabled to explain why
  if (disabled && parentLevel && !listEntities.isFetching) {
    return (
      <Tooltip
        content={
          <DisabledHint
            currentLabel={customerLabel}
            parentLevel={parentLevel}
            parentLabel={parentLabel}
            filters={filters}
          />
        }
        position="bottom"
        delay
      >
        <div className="flex h-full items-end">{selectComponent}</div>
      </Tooltip>
    );
  }

  return selectComponent;
}
